/**
 * PermitRequirementsSummary — compact readiness header (count + %).
 * Used inside the Permit detail dialog and the PermitQuickViewModal.
 */
import { ClipboardList, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { RequirementReadiness } from "@/data/permitting/requirementsChecklist";

interface Props {
  readiness: RequirementReadiness;
  /** When true, render in a single-line compact form for embedded contexts. */
  compact?: boolean;
  /** Profile label (e.g. "City of Tampa Construction Services" or "Generic"). */
  profileLabel?: string;
}

export function PermitRequirementsSummary({ readiness, compact, profileLabel }: Props) {
  const { applicable, complete, pending, notApplicable, readinessPct } = readiness;
  const pctLabel = readinessPct === null ? "N/A" : `${readinessPct}%`;
  const tone =
    readinessPct === null ? "text-muted-foreground" :
    readinessPct >= 100 ? "text-success" :
    readinessPct >= 60 ? "text-foreground" :
    "text-warning";

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={`tabular-nums font-medium ${tone}`}>{pctLabel}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground tabular-nums">{complete}/{applicable - notApplicable} ready</span>
        {pending > 0 && (
          <Badge variant="outline" className="text-[10px] gap-1 border-warning/40 text-warning">
            <AlertTriangle className="h-3 w-3" /> {pending} open
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-3 bg-muted/20">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Permit packet readiness</p>
          {profileLabel && <p className="text-[11px] text-muted-foreground/80">{profileLabel}</p>}
        </div>
        <p className={`text-2xl font-semibold tabular-nums ${tone}`}>{pctLabel}</p>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
        <div><span className="text-muted-foreground">Complete: </span><span className="text-foreground tabular-nums">{complete}</span></div>
        <div><span className="text-muted-foreground">Pending: </span><span className="text-foreground tabular-nums">{pending}</span></div>
        <div><span className="text-muted-foreground">N/A: </span><span className="text-foreground tabular-nums">{notApplicable}</span></div>
      </div>
    </div>
  );
}
